import type { UserPermissionWithPath } from 'api';
import { firmIntelligenceUrl } from 'common/config/urls';
import { PORTFOLIO_VALUATION } from 'common/constants/auth';
import { FUND_PERMISSION_NAME } from 'common/constants/sidebar';
import { COMPANY_NAME_TITLE } from 'common/constants/summary';
import { dashboardCompanyOverviewUrl, WORKBOARD_CURRENT_VALUATIONS_URL } from 'dashboard409a/common/config/urls';
import { getPreviousWorkboardRoute } from 'dashboard409a/utilities/navigation';
import type { CompanySelectorPermission } from 'pages/Auth/components/CompanySelector/CompanySelector.props';
import type { LoginFirm } from 'pages/Auth/components/FirmList';
import { clearAppAutoSelect, getAppAutoSelect, getSafePreviousPortfolioRoute } from 'pages/Auth/utils';
import type { WorkspaceLocationState, WorkspaceRedirectUser } from './useWorkspaceAutoRedirects.types';

interface ResolveWorkspaceAutoRedirectParams {
  hasNotFirmPermissions: boolean;
  isCompanyProduct: boolean;
  locationState?: WorkspaceLocationState | null;
  lowerPermissions: UserPermissionWithPath[];
  resolvedFirmList: LoginFirm[] | null;
  resolvedStandaloneCompaniesPermissions: CompanySelectorPermission[];
  resolvedUser?: WorkspaceRedirectUser;
}

export type WorkspaceAutoRedirectPlan =
  | {
      kind: 'clear-app-auto-select';
    }
  | {
      kind: 'deep-link' | 'portfolio-resume' | 'single-firm' | 'single-company' | 'staff-company';
      target: string;
    }
  | {
      kind: 'lower-permissions';
      permission: UserPermissionWithPath;
      target: string;
    };

const getSelectedLowerPermission = (lowerPermissions: UserPermissionWithPath[]) => {
  // Restricted users can have multiple lower permissions when they have access to a fund
  // and the companies in which that fund invested; prefer the fund route in that case.
  const fundPermission = lowerPermissions.find(
    ({ feature_object }) => feature_object?.object_type === FUND_PERMISSION_NAME
  );

  return lowerPermissions.length > 1 && fundPermission ? fundPermission : lowerPermissions[0];
};

const getPathname = (route: string) => route.split(/[?#]/)[0] ?? '';

const trimBoundarySlashes = (path: string) => {
  let start = 0;
  let end = path.length;

  while (path[start] === '/') {
    start += 1;
  }
  while (end > start && path[end - 1] === '/') {
    end -= 1;
  }

  return path.slice(start, end);
};

const normalizePath = (route: string) => trimBoundarySlashes(getPathname(route));

const getFirmSlugFromPortfolioRoute = (route: string) => {
  const [root, firmSlug] = normalizePath(route).split('/');

  return root === 'firms' ? firmSlug || null : null;
};

const getPermissionObjectBasePath = (permissionPath?: string) => {
  if (!permissionPath) {
    return null;
  }

  const segments = normalizePath(permissionPath).split('/');
  const objectPathIndex = segments.findIndex(segment => segment === 'companies' || segment === 'funds');

  if (objectPathIndex === -1) {
    return normalizePath(permissionPath);
  }

  const objectSlug = segments[objectPathIndex + 1];

  if (!objectSlug) {
    return null;
  }

  return segments.slice(0, objectPathIndex + 2).join('/');
};

const userCanAccessLowerPermissionRoute = (route: string, lowerPermissions: UserPermissionWithPath[]) => {
  const routePath = normalizePath(route);

  return lowerPermissions.some(permission => {
    const permissionObjectBasePath = getPermissionObjectBasePath(permission.feature_object_path);

    return permissionObjectBasePath
      ? routePath === permissionObjectBasePath || routePath.startsWith(`${permissionObjectBasePath}/`)
      : false;
  });
};

const userCanAccessSavedPortfolioRoute = ({
  lowerPermissions,
  resolvedFirmList,
  resolvedUser,
  route,
}: {
  lowerPermissions: UserPermissionWithPath[];
  resolvedFirmList: LoginFirm[] | null;
  resolvedUser?: WorkspaceRedirectUser;
  route: string;
}) => {
  const firmSlug = getFirmSlugFromPortfolioRoute(route);

  const matchingFirm = resolvedFirmList?.find(firm => firm.slug === firmSlug);

  if (matchingFirm && (resolvedUser?.is_superuser || (matchingFirm.user_permissions?.length ?? 0) > 0)) {
    return true;
  }

  return userCanAccessLowerPermissionRoute(route, lowerPermissions);
};

const getPortfolioResumePlan = ({
  isCompanyProduct,
  lowerPermissions,
  resolvedFirmList,
  resolvedUser,
}: ResolveWorkspaceAutoRedirectParams): WorkspaceAutoRedirectPlan | null => {
  if (isCompanyProduct || getAppAutoSelect() !== PORTFOLIO_VALUATION) {
    return null;
  }

  const resume = getSafePreviousPortfolioRoute(resolvedUser?.id);

  if (
    !resume ||
    !userCanAccessSavedPortfolioRoute({
      lowerPermissions,
      resolvedFirmList,
      resolvedUser,
      route: resume,
    })
  ) {
    return null;
  }

  return {
    kind: 'portfolio-resume',
    target: resume,
  };
};

const getDirectWorkspacePlan = ({
  hasNotFirmPermissions,
  isCompanyProduct,
  resolvedFirmList,
  resolvedStandaloneCompaniesPermissions,
  resolvedUser,
}: ResolveWorkspaceAutoRedirectParams): WorkspaceAutoRedirectPlan | null => {
  const firmSlug =
    !isCompanyProduct && !hasNotFirmPermissions && resolvedFirmList?.length === 1 ? resolvedFirmList[0]?.slug : null;
  if (firmSlug) {
    return {
      kind: 'single-firm',
      target: firmIntelligenceUrl(firmSlug),
    };
  }

  const companySlug =
    isCompanyProduct && resolvedStandaloneCompaniesPermissions.length === 1
      ? resolvedStandaloneCompaniesPermissions[0]?.slug
      : null;
  if (companySlug) {
    return {
      kind: 'single-company',
      target: dashboardCompanyOverviewUrl(companySlug),
    };
  }

  if (isCompanyProduct && resolvedUser?.is_staff) {
    // Company Valuation staff resume Workboard; Portfolio users continue to the firm selector.
    return {
      kind: 'staff-company',
      target: getPreviousWorkboardRoute(resolvedUser.id) || WORKBOARD_CURRENT_VALUATIONS_URL,
    };
  }

  return null;
};

const getLowerPermissionsPlan = ({
  hasNotFirmPermissions,
  lowerPermissions,
}: ResolveWorkspaceAutoRedirectParams): WorkspaceAutoRedirectPlan | null => {
  if (!hasNotFirmPermissions || lowerPermissions.length === 0) {
    return null;
  }

  const permission = getSelectedLowerPermission(lowerPermissions);
  const target = permission?.feature_object_path;

  return permission && target
    ? {
      kind: 'lower-permissions',
      permission,
      target: `/${target}`,
    }
    : null;
};

export const applyWorkspaceAutoRedirectPlanSideEffects = async ({
  fetchCompanyInfo,
  fetchFundInfo,
  markRestrictedUser,
  plan,
}: {
  fetchCompanyInfo: (companyId: number) => Promise<void> | void;
  fetchFundInfo: (fundId: number) => Promise<void> | void;
  markRestrictedUser: () => void;
  plan: WorkspaceAutoRedirectPlan;
}) => {
  if (plan.kind === 'clear-app-auto-select') {
    clearAppAutoSelect();
    return;
  }

  if (plan.kind !== 'lower-permissions') {
    clearAppAutoSelect();
    return;
  }

  const featureObject = plan.permission.feature_object;

  // Downstream firm/company containers still rely on this global restricted-user flag
  // and the matching object info being hydrated before navigation.
  markRestrictedUser();

  try {
    if (featureObject?.object_type === COMPANY_NAME_TITLE) {
      await fetchCompanyInfo(featureObject.object_id);
    } else if (featureObject?.object_id) {
      await fetchFundInfo(featureObject.object_id);
    }
  } finally {
    clearAppAutoSelect();
  }
};

export const resolveWorkspaceAutoRedirectPlan = (
  params: ResolveWorkspaceAutoRedirectParams
): WorkspaceAutoRedirectPlan | null => {
  const { locationState } = params;
  const pathname = locationState?.from?.pathname;
  const search = locationState?.from?.search;

  if (pathname && search) {
    return {
      kind: 'deep-link',
      target: `${pathname}${search}`,
    };
  }

  // Saved routes are navigation hints, so permissions must be checked again
  // before they can outrank deterministic single-option routing.
  const portfolioResumePlan = getPortfolioResumePlan(params);
  if (portfolioResumePlan) {
    return portfolioResumePlan;
  }

  const directWorkspacePlan = getDirectWorkspacePlan(params);
  if (directWorkspacePlan) {
    return directWorkspacePlan;
  }

  return getLowerPermissionsPlan(params);
};
